import http, { isErrorToastedByHttpClient } from '@/lib/http'
import { u } from '@/lib/url'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'

// Only e-Surgery offers WhatsApp
const ESURGERY_VENDOR_HASH = '9dabf1cc4105e0b5575366a2b81d6664'

export type WhatsAppConsent = {
  optedIn: boolean
  phone: string | null
  source: 'checkout' | 'whatsapp-reply' | 'support'
  decidedAt: string
  agent: { id: string; name: string } | null
}

const WHATSAPP_CONSENT_QUERY_KEY = 'whatsapp-consent'
const whatsAppConsentQueryKey = (customerHash: string) => [WHATSAPP_CONSENT_QUERY_KEY, customerHash]

export const useWhatsAppConsentQuery = (customerHash: string) =>
  useQuery({
    queryKey: whatsAppConsentQueryKey(customerHash),
    queryFn: async () => {
      const { consent } = (await http.get(u`/whatsapp-consent/${customerHash}`, {
        params: { vendorHash: ESURGERY_VENDOR_HASH }
      })) as { consent: WhatsAppConsent | null }
      return consent
    }
  })

export const useChangeWhatsAppConsentMutation = (customerHash: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (optedIn: boolean) =>
      http.put(u`/whatsapp-consent/${customerHash}`, { vendorHash: ESURGERY_VENDOR_HASH, optedIn }),
    onSuccess: (_, optedIn) => {
      enqueueSnackbar(optedIn ? 'WhatsApp switched on' : 'WhatsApp switched off', { variant: 'success' })
    },
    onError: error => {
      if (!isErrorToastedByHttpClient(error)) {
        enqueueSnackbar('Could not confirm the change. Check the WhatsApp choice before trying again', {
          variant: 'error'
        })
      }
    },
    // Also on failure: a timed-out change may still have been stored. Every account's, since off covers each one on the number
    onSettled: () => queryClient.invalidateQueries({ queryKey: [WHATSAPP_CONSENT_QUERY_KEY] })
  })
}
